import { useEffect, useRef, useState } from 'react'
import type { Game, Prediction } from '../../types'

// Matches --motion-slow (tokens.css) — the duration of the card's own
// left-accent/score entrance animation (GameCard.css). The header count only
// updates once that animation finishes, so the two must stay in sync.
const PREDICTION_ENTER_ANIMATION_MS = 320

/** Tracks which fixtures just flipped from unpredicted to predicted (only observable on
 * the grid re-render after the user returns from editing) so their
 *  cards can play a one-time entrance animation. */
export function usePredictionEntrance(
  gameweek: number,
  games: Game[],
  predictions: Record<string, Prediction>
) {
  const [enteringGameIds, setEnteringGameIds] = useState<Set<string>>(new Set())
  const prevPredictedIdsRef = useRef<Set<string> | null>(null)
  const prevGameweekRef = useRef<number | null>(null)

  useEffect(() => {
    const currentIds = new Set(games.filter((g) => predictions[g.id]).map((g) => g.id))
    const isNewGameweek = prevGameweekRef.current !== gameweek
    const previousIds = prevPredictedIdsRef.current

    prevGameweekRef.current = gameweek
    prevPredictedIdsRef.current = currentIds

    if (isNewGameweek || previousIds === null) {
      setEnteringGameIds(new Set())
      return
    }

    const newlyPredicted = Array.from(currentIds).filter((id) => !previousIds.has(id))
    if (newlyPredicted.length === 0) return

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return

    setEnteringGameIds(new Set(newlyPredicted))
    const timer = setTimeout(() => setEnteringGameIds(new Set()), PREDICTION_ENTER_ANIMATION_MS)
    return () => clearTimeout(timer)
  }, [gameweek, games, predictions])

  return { enteringGameIds }
}
